// ── What a record is ACTUALLY asking (one rule, every surface) ─────────────
// Jeff's bug (2026-10-23): a TEST record sat in the queue showing
// "Nothing is blocking this right now" with an Approve button — but nothing
// was being asked, and the record's own next step was the close-out
// ("Next: CLOSE — archive pass, docs updates, carry-forward, ledger
// scorecard"). Jeff: "I can't tell what to approve."
//
// These helpers are the single definition of "what does this row want":
//   - the row's CONTEXT is the record's real next step, never "nothing is
//     blocking" (that line only ever means "nobody is waiting on Jeff");
//   - the row's ACTION is the verb that fits what the record actually
//     needs — Approve only when an approval is really being asked.
//
// The Needs Me deck, the In Flight WITH YOU rail and the Projects board's
// "Waiting on you" deck all read these, so the surfaces cannot drift apart.

// "Nothing is blocking this right now" is what the boards write when NOBODY
// is waiting on Jeff. It is never a question, so it is never the headline.
export const saysNothingBlocking = (line) => {
  const text = String(line ?? "").trim();
  if (!text) return true;
  return /nothing is blocking|\(no blockers\)|^none\b|^no blockers\b/i.test(text);
};

// The record's real next step, in the boards' own words. "Next: CLOSE — …"
// → "CLOSE — …" (the "Next:" prefix is the board's label, not the step).
export const nextStep = (payload = {}) => {
  const raw = String(payload.next || payload.resume?.next || "").trim();
  if (!raw) return "";
  return raw.replace(/^next:\s*/i, "").trim();
};

// The gate a phase runner is holding at, or -1 when no gate is waiting.
export const pendingGate = (payload = {}) => {
  const phases = payload.runner?.phases;
  if (!Array.isArray(phases)) return -1;
  return phases.findIndex((ph) => ph.gate && !ph.gate_approved);
};

// The record is done being worked on and its own next step is the close-out:
// either the step says CLOSE, or every check (or every phase) has passed.
//
// "All phases pass gates" — every checkpoint the runner holds at has been
// approved, so nothing is waiting on Jeff at any gate.
export const isCloseOut = (state, payload = {}) => {
  if (state !== "TEST") return false;
  if (/\bclose\b/i.test(nextStep(payload))) return true;
  const checks = payload.successTest || payload.detail?.successTest;
  if (
    checks &&
    typeof checks.total === "number" &&
    checks.total > 0 &&
    (checks.checked || 0) >= checks.total
  ) {
    return true;
  }
  const phases = payload.runner?.phases;
  if (!Array.isArray(phases) || !phases.length) return false;
  // every gate the runner holds at has been approved
  if (phases.some((ph) => ph.gate && !ph.gate_approved)) return false;
  // and the phases themselves are done
  if (phases.every((ph) => ph.status === "done")) return true;
  return false;
};

// The one-line context for a record: the blocker when it really is blocked
// on Jeff, the real next step when it is not. Empty means the record asks
// nothing at all — it is not queue-worthy.
//
// A phase checkpoint waiting on Jeff is a blocker too, so a gated record
// always says so — even when the board's blockersLine says nothing is
// blocking.
//
// A PLAN record always has a real ask: approving it is what moves it to
// EXECUTE (the board's own rules, backend/server.py APPROVE_NEXT).
// So a PLAN record's context falls back to that ask, never to a bare title.
export const recordContext = (payload = {}, state = "") => {
  const blockers = String(payload.blockersLine || "").trim();
  if (blockers && !saysNothingBlocking(blockers)) return blockers;
  const step = nextStep(payload);
  if (step) return `Next: ${step}`;
  const gate = pendingGate(payload);
  if (gate >= 0) {
    const title = payload.runner?.phases?.[gate]?.title;
    return title
      ? `Waiting on your word — phase ${gate + 1} is holding: ${title}`
      : `Waiting on your word — phase ${gate + 1} is holding`;
  }
  if (state === "PLAN") return "Next: EXECUTE";
  return "";
};

// What a record is asking Jeff for, and the label that fits it:
//   gate    → a named checkpoint is waiting: "Approve & continue"
//   approve → the record is blocked on his word: "Approve"
//   close   → the record's own next step is the close-out: "Close it out"
//   open    → nothing to approve; the record is here to be read: "Open it"
//
// A goal never has a phase gate to clear, so it starts at the approval test.
//
// The close case: the board's own rules move a TEST record to CLOSE
// on approval (backend/server.py APPROVE_NEXT = {PLAN: EXECUTE, TEST: CLOSE}),
// so "Close it out" IS that approval — the same verb, an honest label, and
// the record lands in the state its own next step named. There is no separate
// close verb to hunt for.
export const recordAsk = (state, payload = {}) => {
  const gate = pendingGate(payload);
  if (gate >= 0) return { kind: "gate", label: "Approve & continue", gate };
  if (state === "PLAN") return { kind: "approve", label: "Approve" };
  if (isCloseOut(state, payload)) return { kind: "close", label: "Close it out" };
  const blockers = String(payload.blockersLine || "").trim();
  if (blockers && !saysNothingBlocking(blockers)) {
    return { kind: "approve", label: "Approve" };
  }
  return { kind: "open", label: "Open it" };
};
